import styles from "./ChatBox.module.css";
import Icon from "@lib/common/components/Icon/Icon";
import type { PaperProps, TextareaProps } from "@mantine/core";
import { Button, Group, Paper, Stack, Text, Textarea } from "@mantine/core";
import { useField } from "@mantine/form";
import { isString } from "lodash-es";
import type { KeyboardEvent, Ref } from "react";
import { useCallback, useMemo } from "react";

const MAX_MESSAGE_LENGTH = 10_000;

export default function ChatBox({
  ref,
  onSubmit,
  onCancel,
  disabled = false,
  isStreaming = false,
  inputSuggestion,
  textareaProps = {},
  paperProps
}: {
  ref: Ref<HTMLTextAreaElement>;
  onSubmit: (message: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  inputSuggestion?: string | null;
  textareaProps?: TextareaProps;
  paperProps?: Partial<PaperProps>;
}) {
  const field = useField({
    initialValue: "",
    validateOnChange: true,
    validate: value => (value.length > MAX_MESSAGE_LENGTH ? "Message too long" : null)
  });

  // Destructure without error — we render our own counter instead of Mantine's default error UI
  const { value: inputValue, onChange: fieldOnChange, onBlur } = field.getInputProps();
  const isOverLimit = !!field.error;

  const submit = useCallback(() => {
    const message = field.getValue().trim();
    if (!message || isOverLimit) {
      return;
    }
    onSubmit(message);
    field.setValue("");
  }, [field, isOverLimit, onSubmit]);

  const hasInputSuggestion = useMemo(() => isString(inputSuggestion) && inputSuggestion.length > 0, [inputSuggestion]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !isStreaming) {
        e.preventDefault();
        submit();
      } else if (e.key === "Tab" && hasInputSuggestion && field.getValue() === "") {
        e.preventDefault();
        // Auto-insert inputSuggestion on Tab.
        field.setValue((inputSuggestion ?? "").replace("e.g. ", ""));
        e.currentTarget.focus();
      }
    },
    [isStreaming, submit, hasInputSuggestion, field, inputSuggestion]
  );

  const submitDisabled = disabled || !inputValue.trim() || isOverLimit;
  const showCounter = inputValue.length >= MAX_MESSAGE_LENGTH - 500;
  const placeholder = useMemo(() => {
    if (hasInputSuggestion) {
      return inputSuggestion + "  (press Tab to use suggestion)";
    }
    return textareaProps.placeholder ?? "";
  }, [hasInputSuggestion, inputSuggestion, textareaProps.placeholder]);

  return (
    <Paper
      classNames={{ root: styles.textareaPaperWrapper }}
      shadow="xxs"
      px="sm"
      pt={5}
      pb="xs"
      withBorder
      w="100%"
      radius="md"
      {...paperProps}
    >
      <Stack gap="xs" align="space-between" h="100%">
        <Textarea
          ref={ref}
          {...textareaProps}
          placeholder={placeholder}
          value={inputValue}
          // Compose handlers to ensure both internal state and any external
          // onChange (e.g., typewriter animation control) are called
          onChange={e => {
            fieldOnChange(e);
            textareaProps.onChange?.(e);
          }}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          size="md"
          autoFocus
          data-autofocus
          autosize={textareaProps.autosize ?? true}
          minRows={textareaProps.minRows ?? 1}
          maxRows={textareaProps.maxRows ?? 6}
          variant="unstyled"
          radius="md"
          fw={500}
        />

        <Group justify="flex-end" align="flex-end">
          {showCounter && (
            <Text size="xs" c={isOverLimit ? "red" : "dimmed"}>
              {inputValue.length.toLocaleString()}/{MAX_MESSAGE_LENGTH.toLocaleString()}
            </Text>
          )}
          {isStreaming ? (
            <Button color="gray" variant="light" size="xs" miw="unset" px="xs" onClick={onCancel}>
              <Icon name="stop" />
            </Button>
          ) : (
            <Button
              color="var(--mantine-primary-color-filled)"
              variant="filled"
              size="xs"
              miw="unset"
              px="xs"
              disabled={submitDisabled}
              onClick={submit}
            >
              <Icon name="arrow-up" />
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}
